"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Check, Navigation, Globe } from "lucide-react"
import { useTranslations } from "next-intl"

/**
 * Type + City pickers, as one black popover.
 *
 * The filter bar used to lay all three axes out horizontally — country, city,
 * type — and the row simply ran out of width. Options did not wrap or
 * collapse, they scrolled off the right edge, and a filter nobody can see is
 * a filter nobody uses.
 *
 * The flags stay inline: eight of them are compact, and a row of countries is
 * the platform saying "we are in eight of these" the moment the page loads —
 * that is worth the width. Type and city are long, unbounded lists, so they
 * move behind a trigger. City is also already reachable by typing in the
 * search bar above, which indexes city and state.
 *
 * Panel styling is lifted from ListingsSortModal deliberately: same radius,
 * same black, same ring, same animation. Two popovers on one screen that
 * differ slightly read as a bug.
 */

export interface FilterOption {
  value: string
  label: string
}

export function ListingsFilterModal({
  open,
  onOpenChange,
  types,
  selectedType,
  onTypeChange,
  cities,
  selectedCity,
  onCityChange,
  align = "left",
  activeCount = 0,
  onClear,
  intlMode,
  onIntlModeChange,
  browseMode,
  onBrowseModeChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  types?: FilterOption[]
  selectedType?: string
  onTypeChange?: (value: string) => void
  cities: FilterOption[]
  selectedCity: string
  onCityChange: (value: string) => void
  /** How many filters are on. Drives the Clear affordance in the header. */
  activeCount?: number
  /** Resets every filter — type, city and country. */
  onClear?: () => void
  /**
   * Which of the two browsing orders is on, or null when the reader has
   * picked a sharper one (Newest, Most popular, a price) from the sort menu.
   * This control and that menu write the same state, so they can never
   * disagree; null is simply "neither of these two".
   */
  browseMode?: "near" | "random" | null
  onBrowseModeChange?: (value: "near" | "random") => void
  /**
   * International mode, on the mobile bar only. Off, the row behind this
   * panel lists US cities; on, it lists the country roster. Passed only by
   * the mobile instance — desktop has the width to show both at once and
   * never needs the mode.
   */
  intlMode?: boolean
  onIntlModeChange?: (value: boolean) => void
  /**
   * Which edge the panel PREFERS to hang from — a starting position, not a
   * guarantee. Whatever this says, the panel is measured on open and nudged
   * back inside the viewport if it would overflow, so a trigger near either
   * screen edge stays readable at every breakpoint.
   */
  align?: "left" | "right"
}) {
  // Same strings the sort menu uses — the pill and the menu are two doors
  // into one state, so they must never read differently in Spanish.
  const tSort = useTranslations("pages.listings.sort")
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const panel = panelRef.current
      if (!panel) return
      // The bar renders this twice — once for desktop, once for mobile — and
      // both share one `open` flag, so the hidden copy is still mounted with a
      // live listener. Its wrapper does not contain the visible copy's pills,
      // so every click in the open panel looked "outside" to it and it closed
      // the popover before the pill's own click could fire. A display:none
      // element has no offsetParent; the hidden twin sits this out.
      if (panel.offsetParent === null) return
      // Test against the WRAPPER, not just the panel. The trigger button is a
      // sibling of this panel inside the same relative container, so a click
      // on it was "outside": mousedown closed the popover, then the button's
      // own click toggled it straight back open. It looked like the trigger
      // could open but never close.
      const scope = panel.parentElement ?? panel
      if (!scope.contains(e.target as Node)) onOpenChange(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open, onOpenChange])

  // Keep the panel on screen whatever the trigger's position.
  //
  // `align` picks the side it PREFERS to hang from, but that alone is a guess
  // about where the trigger sits: the desktop City trigger is near the right
  // of a wide bar, so hanging left ran the panel off the edge and cut the
  // second column of cities clean off. Rather than hardcoding a side per
  // breakpoint — which is the same guess, made three times — this measures
  // once on open and nudges the panel back inside the viewport.
  //
  // The nudge is a margin, not a transform: the panel animates in on
  // `zoom-in-95`, which drives transform, and a second transform here would
  // be overridden for the length of the animation and then snap.
  const [shift, setShift] = useState(0)
  useLayoutEffect(() => {
    if (!open) {
      setShift(0)
      return
    }
    // Measure unshifted, then correct, so reopening never compounds the
    // previous nudge.
    setShift(0)
    const id = requestAnimationFrame(() => {
      const el = panelRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const margin = 8
      let dx = 0
      if (r.right > window.innerWidth - margin) dx = window.innerWidth - margin - r.right
      if (r.left + dx < margin) dx = margin - r.left
      setShift(dx)
    })
    return () => cancelAnimationFrame(id)
  }, [open])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      style={shift ? { marginLeft: shift } : undefined}
      className={`absolute z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-3xl bg-[rgba(0,0,0,0.92)] backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.22)] ring-1 ring-white/5 p-3 animate-in fade-in zoom-in-95 duration-200 ${
        align === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"
      }`}
    >
      {/* Header — only earns its space once something is on. Clearing from
          inside the panel matters because the filter that produced an empty
          grid is usually one the reader has scrolled past: without this they
          have to find and un-tap each pill, or hit the empty state's button,
          which they only see after the results are already gone. */}
      {activeCount > 0 && onClear && (
        <div className="flex items-center justify-between gap-2 px-1 pb-2.5 mb-2.5 border-b border-white/10">
          <span className="text-[11px] font-medium text-white/50">
            {activeCount} {activeCount === 1 ? "filter" : "filters"} on
          </span>
          <button
            onClick={() => {
              onClear()
              onOpenChange(false)
            }}
            className="text-[11px] font-medium text-white/80 hover:text-white underline underline-offset-2 transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Type is optional. On desktop the type pills live inline in the bar
          — none selected means all — so this popover carries City alone. */}
      {types && types.length > 0 && onTypeChange && (
        <>
          <Group
            heading="Type"
            options={types}
            selected={selectedType ?? "all"}
            onSelect={(v) => {
              onTypeChange(v)
              onOpenChange(false)
            }}
          />
          <div className="h-px bg-white/10 my-3" />
        </>
      )}

      {/* Cities scroll rather than paginate. The list is as long as the
          countries selected allow, and capping it would hide exactly the
          city someone came looking for. */}
      <Group
        heading="City"
        options={cities}
        selected={selectedCity}
        maxHeight
        onSelect={(v) => {
          onCityChange(v)
          onOpenChange(false)
        }}
      />
      {/* International mode — mobile only, and last in the panel. It is a
          mode, not a filter: Type and City are what someone opens this for,
          so they get the top of the panel and the switch sits under them
          where it is out of the way until it is wanted. */}
      {onIntlModeChange && (
        <>
          <div className="h-px bg-white/10 my-3" />
          <button
            type="button"
            role="switch"
            aria-checked={!!intlMode}
            onClick={() => onIntlModeChange(!intlMode)}
            className="w-full flex items-center justify-between gap-3 px-1 py-1.5 text-left group"
          >
            {/* Label only. A line of subtext under a switch is explaining
                what the switch already says, and "International / US shops
                only" managed to read as a contradiction. */}
            <span className="text-[11px] font-medium text-white">International</span>
            {/* Hand-rolled rather than components/ui/switch: this panel is
                always black regardless of theme, and the shared switch takes
                its track and thumb from theme tokens that invert underneath
                it. Same size and travel, fixed colours. */}
            <span
              aria-hidden
              className={`relative flex-shrink-0 inline-flex items-center rounded-full transition-colors ${
                intlMode ? "bg-white" : "bg-white/25 group-hover:bg-white/35"
              }`}
              style={{ width: 32, height: 18 }}
            >
              <span
                className={`absolute rounded-full transition-all ${intlMode ? "bg-black" : "bg-white"}`}
                style={{ width: 14, height: 14, left: intlMode ? 16 : 2 }}
              />
            </span>
          </button>
        </>
      )}

      {/* Near me / Explore — the order, under International, which is the
          scope. The two compose rather than compete: International decides
          which shops are in the list, this decides which of them is on top.
          With International off, "near me" still means something to a reader
          outside the US — it surfaces the closest US shops to them, which is
          what someone within driving distance of a border actually wants. */}
      {onBrowseModeChange && (
        <>
          <div className="h-px bg-white/10 my-3" />
          <div className="flex items-center gap-1 rounded-full bg-white/10 p-0.5">
            {([
              { value: "near" as const, Icon: Navigation },
              { value: "random" as const, Icon: Globe },
            ]).map(({ value, Icon }) => {
              const active = browseMode === value
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onBrowseModeChange(value)}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                    active ? "bg-white text-black" : "text-white/70 hover:text-white"
                  }`}
                >
                  <Icon className="w-3 h-3" strokeWidth={1.75} />
                  {tSort(value)}
                </button>
              )
            })}
          </div>
        </>
      )}

    </div>
  )
}

function Group({
  heading,
  options,
  selected,
  onSelect,
  maxHeight,
}: {
  heading: string
  options: FilterOption[]
  selected: string
  onSelect: (value: string) => void
  maxHeight?: boolean
}) {
  return (
    <div>
      <p className="text-[11px] font-medium text-white/50 px-1 mb-1.5">{heading}</p>
      <div
        className={`flex flex-wrap gap-1.5 ${
          maxHeight
            ? "max-h-44 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.25)_transparent]"
            : ""
        }`}
      >
        {options.map((opt) => {
          const active = opt.value === selected
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                active ? "bg-white text-black" : "bg-white/8 text-white/80 hover:bg-white/15"
              }`}
            >
              {opt.label}
              {active && <Check className="w-3 h-3" strokeWidth={2.5} />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
